<template>
  <div>
    <CfsTable
      v-bind="$attrs"
      ref="dataTable"
      :loading="loading"
      fixed-header
      :headers="headers"
      :items="items"
      class="cfs-virtual-table"
      :options.sync="computedSortOptions"
      disable-pagination
      hide-default-footer
      :table-name="tableName"
      server-sort
      v-on="$listeners"
    >
      <template v-for="header in getHeadersSlots()" #[header]="{ item, value, header }">
        {{ getDisplayValue(value, item, header) }}
      </template>
      <template v-for="(_, name) in $scopedSlots" #[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
      <!-- Display loading skeleton when scrolling downwards -->
      <template v-if="!hideIntersectionLoader" #body.append>
        <div
          v-if="!!items?.length"
          v-intersect="onIntersect"
          class="h-[20px] w-full absolute bottom-0"
        />
        <template v-if="loading && !hideTableLoader">
          <tr>
            <td :colspan="headers?.length" align="center">
              <v-progress-circular color="primary" indeterminate :size="40" />
            </td>
          </tr>
        </template>
      </template>
    </CfsTable>
    <div v-show="showTotal" class="flex justify-between items-center px-3 py-3">
      <div class="flex items-center space-x-4">
        <div class="font-medium text-sm tracking-wide text-primary">
          <p v-if="!loading && totalItems >= items.length">
            Showing {{ items?.length }} / {{ totalItems }} results
          </p>
          <p v-else-if="!loading">Showing {{ items?.length }} results</p>
          <p v-else>
            Fetching more results
            <v-progress-circular class="ml-2" :width="2" :size="14" color="primary" indeterminate />
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-btn v-if="showLoadMore" color="primary" @click="$emit('load')">Load More</v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { DataOptions, DataTableHeader } from 'vuetify';
import { VirtualSortType } from '@/types/virtual-search';
import { VIRTUAL_SORT_TYPE } from '@/constants/virtual-search';
import { useCfsTableStore } from '@/store/modules/components/useCfsTableStore';
import { storeToRefs } from 'pinia';
import date, { DateFormat, formatDateWithTimezone, isValidDate } from '@/extensions/filters/date';

export default defineComponent({
  props: {
    tableName: {
      type: String,
      required: true,
      default: '',
    },
    headers: {
      type: Array as PropType<DataTableHeader[]>,
      default: () => [],
    },
    items: {
      type: Array as PropType<unknown[]>,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    options: {
      type: Object as PropType<VirtualSortType>,
      default: () => {},
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hideIntersectionLoader: {
      type: Boolean,
      default: false,
    },
    hideTableLoader: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    sortOptions: {
      type: Object as PropType<VirtualSortType>,
      default: () => ({}),
    },
    showTotal: {
      type: Boolean,
      default: true,
    },
    enabled: {
      type: Boolean,
      default: true,
    },
    showLoadMore: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['load', 'update:sort-options'],
  setup(props, { emit }) {
    const cfsTableStore = useCfsTableStore();
    const { headers } = storeToRefs(cfsTableStore);
    const onIntersect = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      const [intersectObserver] = entries;
      intersectObserver?.isIntersecting && emit('load');
    };

    const getHeadersSlots = () => {
      const tableHeaders = headers.value?.[props.tableName] || [];
      return tableHeaders.map(({ value }) => `item.${value}`);
    };

    const getDisplayValue = (value: unknown, item: any, header: any) => {
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (isValidDate(value)) {
        const timeZone = item?.[`${header.value}TimeZone`];
        const dateFormat = header?.dateFormat ?? DateFormat.Default;

        return timeZone
          ? formatDateWithTimezone(value as string, dateFormat, timeZone)
          : date(value as string, dateFormat);
      }
      return value ? value : '---';
    };
    const computedSortOptions = computed({
      get() {
        return {
          sortBy: [props.sortOptions.name],
          sortDesc: [props.sortOptions.order === VIRTUAL_SORT_TYPE.DESC],
        };
      },
      set(options: DataOptions) {
        if (!props.enabled) return;
        onOptionsUpdate(options);
      },
    });

    const onOptionsUpdate = ({ sortBy: [sortBy], sortDesc: [sortDesc] }: DataOptions) => {
      if (!sortBy) return;
      emit('update:sort-options', {
        name: sortBy.replace('.keyword', ''),
        order: sortDesc ? VIRTUAL_SORT_TYPE.DESC : VIRTUAL_SORT_TYPE.ASC,
      });
    };

    return {
      computedSortOptions,
      onIntersect,
      onOptionsUpdate,
      getHeadersSlots,
      getDisplayValue,
    };
  },
});
</script>

<style lang="scss">
.cfs-virtual-table {
  tbody {
    position: relative;
  }
}
</style>
